/**
 * tools/api.ts — the thin client for the Stage 3.4 Dataverse routes, shared by the three Stage 3.5
 * pages (Catalogue, Share, ToolDetail).
 *
 * OWNERSHIP (db_auth_flow_spec Part 1): every call here goes same-origin to OUR gateway (`/api/…`
 * via the Vite proxy), sending the app session cookie. The browser NEVER talks to Microsoft, NEVER
 * holds a token, NEVER sends a Dataverse URL or an owner id. The only Microsoft host that appears
 * anywhere on the frontend is the make.powerautomate.com DEEP LINK, composed client-side from ids
 * the backend already returned (see powerAutomateDeepLink) — never fetched.
 *
 * FEATURE FLAG (FEATURE_DATAVERSE_ARTEFACT): when the backend flag is off, these route modules are
 * not mounted, so a request 404s. The frontend has no other channel to learn the flag, so a 404 on
 * a Dataverse route is read as "feature disabled" and the page degrades gracefully (never crashes).
 *
 * These types mirror the SAFE response shapes the backend emits (db/tool.ts, routes/tools.ts,
 * routes/flows.ts). None of them carries clientdata, a token, a cookie, an auth code, or a header —
 * the backend never puts those on the wire, and this client never asks for them.
 */

import type {
  FlowComponent,
  FlowComponentDoc,
  ModerationMode,
  TagSuggestion,
  ToolSuggestion,
  ToolEngagementCounts,
} from '@trailblazer/shared';
import { isSearchable, MAX_SUGGESTIONS } from '@trailblazer/shared';

// ── Response shapes (mirror the backend's SAFE shapes; NO clientdata / token / secret) ───────────

/** One connector requirement as summarised for the catalogue (safe: type id + boolean only). */
export interface CatalogueConnector {
  apiName: string;
  required: boolean;
}

/** A row in the catalogue list — GET /api/tools (db/tool.ts CatalogueToolSummary). */
export interface CatalogueToolSummary extends ToolEngagementCounts {
  toolId: string;
  name: string;
  description: string;
  ownerName: string;
  mine: boolean;
  publicationStatus: string;
  healthState: string;
  hasClientdata: boolean;
  requiredConnectors: CatalogueConnector[];
  // ── Stage 4 (Tool Discovery) additive SAFE fields ──
  categoryId: number; // FK app.category (matches ToolRefData.categories[].id)
  departmentId: number; // FK app.department (matches ToolRefData.departments[].id)

  /**
   * ── TD-F04: the classification, as a person reads it and as a link they can follow ──
   *
   * The two ids above stay — the owner's edit form binds to them and the filter dropdowns are keyed by
   * them. These are the same two values SPELLED, because a link needs a word on it, and because a card
   * showing `categoryId: 3` told a reader nothing at all. Both are NOT NULL server-side, so neither is
   * nullable and neither needs an absence string.
   *
   * `tags` and `tagLinks` are the SAME tags in two shapes, from one read in one expression on the
   * server — so they cannot disagree about which tags a tool has. `tags` is what the owner's edit
   * control binds to (a comma-joined text box, `ToolDetail.tsx`); `tagLinks` is what TD-F04 renders,
   * because the tag filter takes an id. At most five, capped by a database trigger rather than by any
   * caller, so a surface may render all of them.
   *
   * Every href is built by `tools/catalogueLinks.ts`, so the card and the tool page cannot encode the
   * same filter two different ways.
   */
  categoryLabel: string;
  departmentLabel: string;
  tags: string[]; // display labels; [] when the tool has none
  tagLinks: TagLink[]; // the same tags with their ids, for TD-F04's filtered-view links
  adoptionCount: number; // from clone_event (created_draft); NOT the denormalised column
  lastAdoptedAt: string | null; // ISO timestamp, or null if never adopted
  adoptedByMe: boolean; // the VIEWER'S own adoption only (never who else adopted)
  // Open publishing: Approved => 'Approved', every other visible status => 'Unverified'. The label
  // is the user-facing trust signal; publication_status no longer gates whether a tool is visible.
  trustLabel: 'Approved' | 'Unverified';

  // ── DS-F07: the trust signals a CARD shows — the "Card" column of the tool page's §8 table ──
  //
  // Card ≠ a smaller detail page. Signals 6 (version), 8 (time saved) and 9 (LIKE COUNT) are "Card: no"
  // in that table and are deliberately absent from this payload, so a card cannot render them by
  // reaching for a field that happens to be there. Signal 1 (adoptionCount/lastAdoptedAt) and 4
  // (healthState) are already above. Signal 7 (access) needs nothing: the API's access gate removes a
  // tool the viewer may not use from the page entirely, so "if unmet" never happens on a card.
  //
  // Every one of these is FORMATTED through `tools/trustSignals.ts`, which the tool page's Trust list
  // uses too — that shared module, not a convention, is what stops the two surfaces wording the same
  // signal differently (DS-NFR06).
  reviewMarker: 'none' | 'current' | 'lapsed'; // signal 2 — state only; the card shows an icon for it
  recentlyConfirmedWorkingCount: number; // signal 3 — raw count; the display floor is applied on render
  openIssueCount: number; // signal 5 — the card shows the count, the tool page the reports themselves
  ratingAverage: number | null; // signal 10 — null means "not yet rated" (FB-F21), NOT zero stars
  ratingCount: number; // signal 10 — the true count, whether or not the average may be shown

  /**
   * The tool's flow TOPOLOGY, encoded (backend `CatalogueToolSummary.flowShape`) — or null when the
   * definition could not be walked. Hand off to `<FlowArt shape={…} />`; never parse it here.
   *
   * OUTSIDE the DS-F07 block above on purpose, and not a trust signal: that table says what a card
   * SAYS about a tool, and this is the picture beside it. It carries no counts, no dates, no judgement
   * and — by the codec's construction — no author text at all, only shape.
   *
   * Null means "we could not read the flow", and `FlowArt` answers it with an identity emblem rather
   * than a fabricated diagram. Treat the two as equally normal states.
   */
  flowShape: string | null;
}

/** One tag as a TD-F04 click-through renders it — id + label, because the filter takes the id. */
export interface TagLink {
  tagId: string;
  label: string;
}

/** One open issue, WITHOUT the reporter's identity (TD-F09/F20, FB-F06/F10). */
export interface OpenIssue {
  issueId: string;
  kind: 'broken' | 'security' | 'sensitive_data';
  body: string;
  createdAt: string;
}

/**
 * One tool's detail — GET /api/tools/:toolId (db/tool.ts CatalogueToolDetail). Adds the full Tool
 * Detail Page trust-signal set and the feedback actions on top of the pre-existing Stage 3.5 fields
 * above, which this extends without changing.
 */
export interface CatalogueToolDetail extends CatalogueToolSummary {
  problemSolved: string;
  estimatedTimeSavedPerUse: number;
  externalSource: 'dataverse' | 'manual_zip' | null;
  sourceName: string | null;
  // Will's "Requirements" field: the builder's connector setup explanation. null when the tool has none.
  connectorSetupNotes: string | null;
  /**
   * The builder's mandatory per-step setup descriptions (0020 component docs), in execution order —
   * surfaced READ-ONLY as the "Description of connectors" section so an adopter can read how each step
   * is configured. null for a legacy tool whose flow was never walked into components.
   */
  setupSteps: FlowComponentDoc[] | null;

  /**
   * AE-F05 / TD-F17 — how to set the tool up and what it depends on, shown at the handover moment rather
   * than on a page the adopter has to go and find. Both are NOT NULL server-side (required at upload
   * since 0004), so neither is optional here and neither needs an absence string.
   *
   * Distinct from `connectorSetupNotes` (connector-level) and `setupSteps` (per-step): these two are the
   * tool as a whole.
   */
  setupInstructions: string;
  dependencies: string;

  version: number;
  lastUpdated: string;
  // The review marker STATE is on the summary above (the card shows an icon for it); these two are the
  // page's half of signal 2 — §8's "Card: icon only, Detail: full".
  reviewedAt: string | null;
  reviewedVersion: number | null;
  accessRequirementLabel: string;
  accessRuleKind: 'all' | 'department' | 'appointment';
  /** UP-F55: the id behind the label, so the owner panel can pre-select the tool's current value. */
  accessRequirementId: number;
  ownerlessFlag: boolean;
  builderDepartment: string | null;
  builderAvatarRef: string | null;
  builderAvatarKind: 'upload' | 'stock' | null;
  builderPhotoRef: string | null;
  likeCount: number;
  likedByMe: boolean;
  savedByMe: boolean;
  // `ratingAverage`/`ratingCount` and `recentlyConfirmedWorkingCount` are on the summary — both are
  // "Card: yes". What only the tool page may show is what THIS person rated it, and the open issues
  // themselves rather than how many there are (`openIssueCount`, also on the summary).
  myRating: number | null;
  /**
   * The fault reports themselves — EMPTY unless `openIssuesVisibility` is 'full' (TD-F20).
   *
   * TD-F09 wants an open report to be a visible trust signal; the report form promises the report is
   * "Private to the platform owner and the SWAT team". The API keeps both by sending the COUNT to
   * everyone and the BODIES only to the tool's builder, the platform owner and the SWAT team — so
   * `openIssueCount` (on the summary above) is always the true live number, and this list is empty for
   * everyone else. Rendering off `openIssues.length` therefore says "no open issues" about a tool that
   * has them: use `openIssuesDisplay` in `tools/trustSignals.ts`, which branches on the visibility.
   */
  openIssues: OpenIssue[];
  openIssuesVisibility: 'full' | 'count_only';
  /** Why the bodies are withheld, in words this page shows. Null when they are not withheld. */
  openIssuesWithheldReason: string | null;
  showDidItWorkPrompt: boolean;
  /**
   * DEMO MODE ONLY (API `config.DEMO_MODE_ADOPT`, ADR-008 ruling 2) — an approved environment to adopt
   * INTO, served alongside the tool so the Adopt panel can render when `GET /api/flows/mine` cannot
   * answer for want of a Dataverse tenant.
   *
   * OPTIONAL, and absent on every ordinary response including all of production: the API only sets it
   * when the demo flag is on. `ToolDetail.tsx`'s loader treats absence as "no fallback" and behaves
   * exactly as it did before this field existed. Presence is not a licence to skip the note — an
   * adoption recorded against this environment creates no flow in Power Automate.
   */
  demoAdoptTarget?: { environmentId: string; environmentName: string };
  /**
   * AE-F02 / RT-F07 — non-null when this tool cannot be adopted, with the reason to show. `null` on
   * every adoptable tool.
   *
   * The SAME rule the adopt endpoint enforces (API `db/adoptEligibility.ts`), reported here so the
   * page can name the reason and disable the button instead of leaving it live for someone to press
   * and be turned away. Optional so an older API build that does not send it reads as "no block" —
   * the endpoint is the authority either way, and `doAdopt` still checks the response.
   */
  adoptBlockedReason?: AdoptBlockedReason | null;
  /**
   * AE-F04 — `app.tool.artefact_type` as stored. Reported for completeness; the handover below is what
   * the page branches on. Worth knowing what this value is worth: both tool-creation paths in the API
   * write the constant `'link'`, so today it is a placeholder rather than a builder's answer.
   */
  artefactType?: 'file' | 'prompt' | 'link';
  /**
   * AE-F04 — which handover this tool gets, resolved by the API. Optional so an older API build that
   * does not send it reads as "no handover information", and the page falls back to the Adopt panel it
   * has always rendered — which is also what the API returns for every tool on current data.
   */
  handover?: Handover | null;
}

/** One of the signed-in user's cloud flows — GET /api/flows/mine (routes/flows.ts). */
export interface CloudFlowSummary {
  workflowId: string;
  name: string;
  category: number | null;
  type: number | null;
  stateCode: number | null;
  createdOn: string | null;
  /** Dataverse `modifiedon` — the flow's last-edited time (ISO string). Powers the "Modified" column. */
  modifiedOn: string | null;
  createdByMatch: boolean;
  ownerMatch: boolean;
}

/**
 * Power Automate's own flow Type, derived by the backend from the flow's TRIGGER (NOT the Dataverse
 * `type` code). `'Unknown'` when the blueprint could not be read — the picker leaves the cell blank.
 * POST /api/tools/classify-trigger.
 */
export type FlowTriggerType = 'Automated' | 'Instant' | 'Scheduled' | 'Unknown';

/** POST /api/tools/classify-trigger success shape. */
export interface ClassifyTriggerResult {
  workflowId: string;
  triggerType: FlowTriggerType;
}

/** GET /api/flows/mine success shape. */
export interface FlowsMineResponse {
  environment_id: string;
  environment_name: string;
  flows: CloudFlowSummary[];
}

/** One owner-editable reference option — GET /api/tools/refdata (db/tool.ts RefOption). */
export interface RefOption {
  id: number;
  label: string;
}

/** GET /api/tools/refdata success shape. */
export interface ToolRefData {
  categories: RefOption[];
  accessRequirements: RefOption[];
  departments: RefOption[];
  /**
   * CAT-F12: the tag vocabulary already in use, so the upload form can offer a colleague's word
   * instead of letting the builder invent a near-duplicate of it.
   *
   * NOT `RefOption[]`. The other three lists are foreign keys the builder must choose from; tags are
   * FREE TEXT the builder may invent (UP-F54a), so the upload body carries LABELS and these rows are
   * a suggestion, not a closed list. They ride along on this call — which the form already makes and
   * blocks on — rather than a `?q=` endpoint, so suggestions appear on the first keystroke instead of
   * after a debounced round trip to the Pi. See api/src/db/tag.ts's `listTagSuggestionsWith`.
   */
  tags: TagSuggestion[];
  /**
   * The platform moderation posture (UP-F45) — surfaced so the upload wizard can LABEL its submit
   * button ('Publish' vs 'Submit for review') to match where the submission will route. Read the same
   * validated way on the server (db/tool.ts listToolRefData) so client and server cannot drift.
   */
  moderationMode: ModerationMode;
}

/**
 * GET /api/tools/builders success shape — the option list for the catalogue's builder filter.
 *
 * A STRING id, unlike RefOption's number: `builder_user_id` is a BIGINT, and the ids in RefOption are
 * SMALLINT/INT reference-list keys. Viewer-dependent (only builders with a tool the viewer can see),
 * so it is a separate call rather than a field on ToolRefData, which is not.
 */
export interface CatalogueBuilderOption {
  id: string;
  label: string;
}

/** POST /api/tools success shape (safe — presence + connector summary, never clientdata). */
export interface UploadResult {
  tool_id: string;
  name: string;
  publication_status: string;
  has_clientdata: boolean;
  connector_count: number;
  required_connectors: CatalogueConnector[] | { ok: false; reason: string };
  /** How many flow components the builder documented — 0 for a non-component upload. */
  documented_component_count?: number;
}

/** The upload request body — exactly the fields routes/tools.ts uploadBody requires. */
export interface UploadRequest {
  targetEnvironmentId: string;
  workflowId: string;
  name: string;
  description: string;
  problem_solved: string;
  primary_category_id: number;
  access_requirement_id: number;
  department_id: number;
  /**
   * OPTIONAL as of the per-component upload flow: when `component_docs` is sent, the backend renders
   * the readable projection of those explanations into this column itself. Still required for the
   * pre-existing single-textarea contract.
   */
  setup_instructions?: string;
  dependencies: string;
  /**
   * One explanation per component of the automation (Will's flow). The backend re-derives the
   * authoritative component list from the blueprint and rejects the upload with
   * `component_docs_incomplete` unless every component is covered — the wizard's own gate is UX, the
   * server's is the guarantee.
   */
  component_docs?: FlowComponentDoc[];
  // Will's "Requirements" field: how to set up the flow's connectors and why they're needed. OPTIONAL
  // on the wire — the backend makes it REQUIRED (422 connector_setup_notes_required) only when the
  // uploaded flow's blueprint actually contains connectors, which only the server can determine.
  connector_setup_notes?: string;
  estimated_time_saved: number;
  /**
   * The data-safety attestation (UP-F23). REQUIRED true on the wire — the wizard gates on it and the
   * backend re-checks it (a false/absent value is a 400). Recorded to app.attestation on success.
   */
  attested: boolean;
  /**
   * The self-vouch tick (RESPONSIBILITY_STATEMENT). true ⇒ the builder takes responsibility, so the
   * tool publishes straight away as a Draft; false ⇒ the tool is held as Pending for the AI SWAT
   * team, becoming Approved once they sign it off. Drives the initial publication_status server-side.
   */
  takes_responsibility: boolean;
  // Up to 5 free-text tags (UP-F54), OPTIONAL. Sent as RAW labels — the backend normalises,
  // de-duplicates, creates-on-first-use and enforces the 5-cap (routes/tools.ts uploadBody +
  // db/tag.ts). Omitted entirely when the builder added none, so a tag-less upload is byte-identical
  // to before. No suggestion/autocomplete layer yet — that needs a vocabulary endpoint (future slice).
  tags?: string[];
}

/**
 * POST /api/tools/inspect-connectors success shape — the pre-detect check used by /share to decide
 * whether to show the Requirements field. SAFE: a boolean + a count only, never clientdata, connector
 * names, a token, an oid, or a URL. The final upload's 422 stays the real enforcer; this is a hint.
 */
export interface InspectConnectorsResult {
  has_connectors: boolean;
  connector_count: number;
}

/**
 * AE-F04 — WHAT AN ADOPTER IS HANDED, decided by the API (`db/handover.ts`) and rendered here.
 *
 * Four modes, one of which the page shows: 'clone' copies a stored Dataverse blueprint into the
 * adopter's environment (the existing Adopt panel), 'prompt' copies the prompt in one action, 'link'
 * opens something outside Trailblazer and says so, 'file' names a file this build cannot serve.
 *
 * THE MODE IS NOT DERIVED IN THE BROWSER, deliberately — same reason `adoptBlockedReason` is not. The
 * API enforces which handover a tool gets (the adopt endpoint refuses to clone a prompt), so a second
 * implementation here could offer a handover the API would reject. See the API module's header for why
 * the stored payload decides this and `artefactType` does not.
 */
export type HandoverMode = 'clone' | 'prompt' | 'link' | 'file';

export interface Handover {
  mode: HandoverMode;
  /** The prompt to copy — non-null only when `mode === 'prompt'`. */
  promptText: string | null;
  /** The URL to open — non-null only when `mode === 'link'`. Always `https://` (0005's CHECK). */
  linkUrl: string | null;
  filename: string | null;
  byteSize: number | null;
  /** Non-null when this handover cannot be completed, carrying the reason to show instead. */
  unavailable: string | null;
  declaredType: 'file' | 'prompt' | 'link';
  /** True when the stored payload is not the kind `declaredType` claims — see the API header. */
  declaredTypeMismatch: boolean;
}

/**
 * Why an adopt was refused (API `db/adoptEligibility.ts`). `code` is stable and safe to branch on;
 * `message` is the sentence to SHOW — it travels with the code so the browser never reconstructs
 * prose from an enum and the two cannot drift apart.
 */
export interface AdoptBlockedReason {
  code: 'not_approved' | 'no_artefact' | 'not_cloneable';
  message: string;
}

/** POST /api/tools/:toolId/adopt success shape (safe — ids + connector summary, never a token). */
export interface AdoptResult {
  ok: boolean;
  /**
   * Literal `true`, not `boolean` — this is the DISCRIMINANT of `AdoptResponse`. The endpoint answers
   * 200 with `adopted: false` when the eligibility gate refuses, so a caller that assumed a 2xx meant
   * success would open a Power Automate deep link for a flow that was never created.
   */
  adopted: true;
  new_workflow_id: string;
  environment_id: string;
  environment_name: string;
  requiredConnectors: CatalogueConnector[];
  bindingAttempted: boolean;
  bindingSucceeded: boolean;
  missingConnections: string[];
  /**
   * False ONLY when the adopt succeeded but the `clone_event` insert failed: the flow exists but the
   * adoption is uncounted, so the shown total must NOT be optimistically incremented (AE §6 STEP-4).
   */
  adoptionRecorded: boolean;
  /** True when no Dataverse call was made (API `config.DEMO_MODE_ADOPT`) — so no flow really exists. */
  demoMode: boolean;
  /** AE-F06 — what was adopted, echoed by the API from the row it read, not from the loaded page. */
  tool_name: string;
  /** AE-F06 — whose tool it is (the builder's display name). */
  builder_name: string;
  /**
   * AE §6 STEP-4 — the adoption total AFTER this adoption. `null` means the API could not read it;
   * show nothing rather than a number, and never fall back to the page's pre-adoption figure.
   */
  adoption_count: number | null;
}

/**
 * The eligibility gate refused (AE-F02 / RT-F07). **200, not 403** — deliberately: the row asks for
 * "return the reason, never hide the action", and this shape carries a sentence a person can read.
 */
export interface AdoptRefused {
  ok: true;
  adopted: false;
  reason: AdoptBlockedReason['code'];
  message: string;
}

/**
 * What POST adopt can answer with when the HTTP call itself succeeded. Narrow on `adopted` BEFORE
 * touching `new_workflow_id` or `environment_id` — they do not exist on the refusal arm, and the
 * literal `true` above is what makes TypeScript enforce that rather than leave it to a code review.
 */
export type AdoptResponse = AdoptResult | AdoptRefused;

// ── Fetch helpers (same-origin, session cookie, JSON) ────────────────────────────────────────────

/**
 * The outcome of a call, normalised so loaders/actions can branch without re-inspecting the Response.
 * `ok` carries the parsed body; `unauthorized` is the signed-out (401) case; `featureDisabled` is the
 * flag-off (404 on a Dataverse route family) case; `error` is any other non-OK, with the status and
 * the backend's `error` code when it sent one (never a token/body dump).
 */
export type ApiResult<T> =
  | { kind: 'ok'; body: T }
  | { kind: 'unauthorized' }
  | { kind: 'featureDisabled' }
  | { kind: 'error'; status: number; code: string | null; detail?: unknown; body?: unknown };

/**
 * A GET's result WITHOUT the feature-disabled arm, for callers that don't ask for the 404→disabled
 * mapping (e.g. the tool-detail fetch, which must distinguish a real `tool_not_found` 404) and for
 * every POST (a write never treats a 404 as "feature off"). Keeps the type honest so the caller's
 * error branch narrows cleanly to the `error` arm.
 */
export type ApiResultNoFeature<T> =
  | { kind: 'ok'; body: T }
  | { kind: 'unauthorized' }
  /** `body` is the whole parsed error body; see normalise() for why `detail` alone is not enough. */
  | { kind: 'error'; status: number; code: string | null; detail?: unknown; body?: unknown };

/** Parse a JSON body defensively; a non-JSON error body becomes null rather than throwing. */
async function readJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Normalise a completed fetch Response into the result union. Shared by every verb below; the
 * feature-flag 404 mapping stays in apiGet, its only caller (a write never reads a 404 that way).
 *
 * A success does NOT imply a body. Every wrapper in this file happens to declare a real JSON shape
 * today and no route in routes/tools.ts answers 204 — but 18 endpoints elsewhere in the API do
 * (routes/me.ts, notifications.ts, learning.ts …), so the first no-content route reached through
 * this client would otherwise break. Calling `res.json()` on an empty body throws SyntaxError, and
 * that rejection escapes the verb helpers and strands the caller: an awaited `save()` never reaches
 * its own `setSaving(false)`, so the UI sits on "Saving…" showing no error even though the write
 * succeeded. So a bodyless success is reported as `body: null`, and — for the same reason — a
 * malformed body under an OK status becomes an `error` result rather than a throw. This function
 * must never reject. Mirrors community/api.ts's normalise exactly.
 */
async function normalise<T>(res: Response): Promise<ApiResultNoFeature<T>> {
  if (res.status === 401) return { kind: 'unauthorized' };
  if (res.ok) {
    // 204 and 205 carry no body by definition; an explicit zero length says so for any status.
    if (res.status === 204 || res.status === 205 || res.headers.get('Content-Length') === '0') {
      return { kind: 'ok', body: null as T };
    }
    // Read as text first so an empty body is distinguishable from a malformed one.
    const text = await res.text();
    if (text.trim().length === 0) return { kind: 'ok', body: null as T };
    try {
      return { kind: 'ok', body: JSON.parse(text) as T };
    } catch {
      return { kind: 'error', status: res.status, code: 'malformed_response' };
    }
  }
  const body = (await readJson(res)) as { error?: string; detail?: unknown } | null;
  // `body` is carried whole as well as split into code/detail. WHY: several routes put their useful
  // fields at the TOP LEVEL of the error body rather than under `detail` — `component_docs_incomplete`
  // answers `{ error, component_count, explained_count, missing }` and `flow_components_unavailable`
  // answers `{ error, reason }`. Forwarding only `body.detail` discarded both, so a caller reading
  // `res.detail.missing` or `res.detail.reason` always saw undefined and fell back to a generic
  // sentence. Additive: no existing caller reads `body` on an error, so nothing else changes.
  return {
    kind: 'error',
    status: res.status,
    code: body?.error ?? null,
    detail: body?.detail,
    ...(body === null ? {} : { body }),
  };
}

/**
 * GET a Dataverse route and normalise the outcome. `treat404AsFeatureDisabled` is true for the
 * catalogue/detail feature probes (a 404 there means the route family is not mounted — flag off);
 * callers that need to distinguish a real 404 (e.g. tool_not_found) pass false and read the error.
 */
export function apiGet<T>(
  path: string,
  opts: { treat404AsFeatureDisabled: true },
): Promise<ApiResult<T>>;
export function apiGet<T>(
  path: string,
  opts?: { treat404AsFeatureDisabled?: false },
): Promise<ApiResultNoFeature<T>>;
export async function apiGet<T>(
  path: string,
  opts: { treat404AsFeatureDisabled?: boolean } = {},
): Promise<ApiResult<T>> {
  const res = await fetch(path, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  // Checked before normalise: an unmounted route family has no error body worth reading.
  if (res.status === 404 && opts.treat404AsFeatureDisabled) return { kind: 'featureDisabled' };
  return normalise<T>(res);
}

/**
 * POST JSON to a Dataverse route and normalise the outcome. The body is a plain object we control —
 * it never contains a token, a Dataverse URL, or an owner id (the trust model: the browser supplies
 * only the target env id and the flow/metadata fields; the backend derives the rest).
 */
export async function apiPost<T>(path: string, body: unknown): Promise<ApiResultNoFeature<T>> {
  return normalise<T>(
    await fetch(path, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }),
  );
}

/** PATCH JSON to a route and normalise the outcome, identically to apiPost. */
export async function apiPatch<T>(path: string, body: unknown): Promise<ApiResultNoFeature<T>> {
  return normalise<T>(
    await fetch(path, {
      method: 'PATCH',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }),
  );
}

/** DELETE a route (no body) and normalise the outcome, identically to apiPost. */
export async function apiDelete<T>(path: string): Promise<ApiResultNoFeature<T>> {
  return normalise<T>(
    await fetch(path, {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }),
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────
// Tool Detail Page feedback & trust (FB-F01..F22, TD-F09/F13/F19/F20/F21/F26) — thin wrappers over
// the routes added to routes/tools.ts. Same trust posture as everything else in this file: no token,
// no owner id ever sent (the actor is always the session).
// ─────────────────────────────────────────────────────────────────────────────────────────────────

export type LikeOutcome = 'liked' | 'already';
export type SaveOutcome = 'saved' | 'already';

/**
 * Adopt a tool. The browser sends ONLY the target environment id — never a Dataverse URL, and never
 * the adopter's identity (the actor is always the session).
 *
 * Narrow on `adopted` before touching the flow ids: a 200 can be the eligibility gate's refusal.
 */
export function adoptTool(toolId: string, targetEnvironmentId: string) {
  return apiPost<AdoptResponse>(`/api/tools/${encodeURIComponent(toolId)}/adopt`, {
    targetEnvironmentId,
  });
}

export function likeTool(toolId: string) {
  return apiPost<{ outcome: LikeOutcome }>(`/api/tools/${encodeURIComponent(toolId)}/like`, {});
}
export function unlikeTool(toolId: string) {
  return apiDelete<{ ok: true }>(`/api/tools/${encodeURIComponent(toolId)}/like`);
}
export function saveTool(toolId: string) {
  return apiPost<{ outcome: SaveOutcome }>(`/api/tools/${encodeURIComponent(toolId)}/save`, {});
}
export function unsaveTool(toolId: string) {
  return apiDelete<{ ok: true }>(`/api/tools/${encodeURIComponent(toolId)}/save`);
}

/** One comment as GET /api/tools/:toolId/comments returns it (db/toolFeedback.ts CommentNode). */
export interface CommentNode {
  commentId: string;
  parentCommentId: string | null;
  authorName: string | null;
  authorUserId: string | null;
  body: string;
  createdAt: string;
  edited: boolean;
  removed: boolean;
  isOwn: boolean;
  isBuilderOfTool: boolean;
  replies: CommentNode[];
}

export interface CommentsPage {
  comments: CommentNode[];
  total: number;
}

export function fetchComments(toolId: string, opts: { limit?: number; offset?: number } = {}) {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.offset) params.set('offset', String(opts.offset));
  const qs = params.toString();
  return apiGet<CommentsPage>(
    `/api/tools/${encodeURIComponent(toolId)}/comments${qs ? `?${qs}` : ''}`,
  );
}

export function postComment(toolId: string, body: string, parentCommentId: string | null = null) {
  return apiPost<{ commentId: string; createdAt: string; looksLikeFaultReport: boolean }>(
    `/api/tools/${encodeURIComponent(toolId)}/comments`,
    { body, parentCommentId },
  );
}

export function editComment(toolId: string, commentId: string, body: string) {
  return apiPatch<{ ok: true }>(
    `/api/tools/${encodeURIComponent(toolId)}/comments/${encodeURIComponent(commentId)}`,
    { body },
  );
}

export function deleteComment(toolId: string, commentId: string) {
  return apiDelete<{ ok: true }>(
    `/api/tools/${encodeURIComponent(toolId)}/comments/${encodeURIComponent(commentId)}`,
  );
}

/** FB-F09: the tool's builder removes someone else's comment, with a reason. */
export function removeCommentByBuilder(toolId: string, commentId: string, reason: string) {
  return apiPost<{ ok: true }>(
    `/api/tools/${encodeURIComponent(toolId)}/comments/${encodeURIComponent(commentId)}/remove`,
    { reason },
  );
}

/** FB-F17: flag a comment for moderation review. No reason needed to file it. */
export function reportComment(toolId: string, commentId: string) {
  return apiPost<{ ok: true; outcome: 'flagged' | 'already' }>(
    `/api/tools/${encodeURIComponent(toolId)}/comments/${encodeURIComponent(commentId)}/report`,
    {},
  );
}

export function postRating(toolId: string, stars: number) {
  return apiPost<{ ok: true }>(`/api/tools/${encodeURIComponent(toolId)}/rating`, { stars });
}

export function postIssueReport(
  toolId: string,
  kind: 'broken' | 'security' | 'sensitive_data',
  body: string,
) {
  return apiPost<{ issueId: string; createdAt: string }>(
    `/api/tools/${encodeURIComponent(toolId)}/issues`,
    { kind, body },
  );
}

export function resolveIssueReport(toolId: string, issueId: string, resolutionNote: string) {
  return apiPost<{ ok: true; resolvedAt: string }>(
    `/api/tools/${encodeURIComponent(toolId)}/issues/${encodeURIComponent(issueId)}/resolve`,
    { resolutionNote },
  );
}

/** FB-F14: on `answer: false` the response's `reportPrefill` opens the issue form pre-filled. */
export function postDidItWork(toolId: string, answer: boolean) {
  return apiPost<{ ok: true; reportPrefill: { kind: 'broken'; body: string } | null }>(
    `/api/tools/${encodeURIComponent(toolId)}/did-it-work`,
    { answer },
  );
}

// ── Catalogue governance (TD-F23) — PlatformOwner (some: SWATMember/Reviewer/builder too) ────────

export function setToolHealth(toolId: string, healthState: 'Healthy' | 'Unmaintained' | 'Broken') {
  return apiPatch<{ ok: true }>(`/api/admin/tools/${encodeURIComponent(toolId)}/health`, {
    healthState,
  });
}

export function editToolClassification(
  toolId: string,
  body: { categoryId?: number; accessRequirementId?: number; tags?: string[] },
) {
  return apiPatch<{ ok: true }>(
    `/api/admin/tools/${encodeURIComponent(toolId)}/classification`,
    body,
  );
}

export function withdrawTool(toolId: string, reason: string) {
  return apiPost<{ ok: true }>(`/api/admin/tools/${encodeURIComponent(toolId)}/withdraw`, {
    reason,
  });
}

/** `reason` is optional here and required for withdraw — a restore's "why" is usually evident from
 *  the withdrawal it reverses, so the API accepts one without the UI demanding it. */
export function restoreTool(toolId: string, reason?: string) {
  return apiPost<{ ok: true }>(
    `/api/admin/tools/${encodeURIComponent(toolId)}/restore`,
    reason ? { reason } : {},
  );
}

export function mergeTools(survivingToolId: string, mergedAwayToolId: string) {
  return apiPost<{ ok: true }>('/api/admin/tools/merge', { survivingToolId, mergedAwayToolId });
}

export function reassignToolOwner(toolId: string, newBuilderUserId: string) {
  return apiPost<{ ok: true }>(`/api/admin/tools/${encodeURIComponent(toolId)}/reassign-owner`, {
    newBuilderUserId,
  });
}

/** Distinct from the upload wizard's own DuplicateCandidate below (GET /api/tools/check-duplicates
 *  — a different endpoint, different callsite): this is the admin governance merge-candidate scan
 *  (GET /api/admin/tools/duplicates). */
export interface AdminDuplicateCandidate {
  toolId: string;
  name: string;
  description: string;
  ownerName: string;
  mine: boolean;
  publicationStatus: string;
  trustLabel: string;
}

export function fetchAdminDuplicateCandidates(toolId: string) {
  return apiGet<{ candidates: AdminDuplicateCandidate[] }>(
    `/api/admin/tools/duplicates?toolId=${encodeURIComponent(toolId)}`,
  );
}

/**
 * CAT-F30: one entry in a tool's change history. `actorName` is resolved server-side — a numeric user
 * id is not an answer to "who changed this" — and is null when the actor was an automatic sweep, or
 * when a since-departed user no longer resolves (the join is a LEFT JOIN so their rows still appear).
 */
export interface ToolAuditEntry {
  auditId: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  actorUserId: string | null;
  actorKind: 'user' | 'job';
  actorName: string | null;
  reason: string | null;
  changedAt: string;
}

export function fetchToolAudit(toolId: string) {
  return apiGet<{ entries: ToolAuditEntry[] }>(
    `/api/admin/tools/${encodeURIComponent(toolId)}/audit`,
  );
}

// ── Client-side Power Automate deep link ────────────────────────────────────────────────────────

/**
 * Compose the make.powerautomate.com deep link for a freshly-adopted flow, entirely client-side from
 * ids the backend already returned. The backend NEVER emits this host (the browser talks only to our
 * gateway); we build the convenience link here so the adopter can open their new Draft flow. Both ids
 * come from the adopt response — no token, no secret, no Dataverse URL is involved.
 */
export function powerAutomateDeepLink(environmentId: string, newWorkflowId: string): string {
  return `https://make.powerautomate.com/environments/${environmentId}/flows/${newWorkflowId}/details`;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────
// The per-component upload flow (Will's automatic Power Automate journey).
//
// The browser NEVER parses a flow definition — it never has one. The backend walks the blueprint and
// returns the derived component manifest below; the wizard renders one required explanation per
// entry. Same trust posture as inspect-connectors: we send an approved-environment id (which came
// from /api/flows/mine) and a workflow id, and nothing else.
// ─────────────────────────────────────────────────────────────────────────────────────────────────

/** The component manifest for one flow, as POST /api/tools/inspect-components returns it. */
export interface InspectComponentsResult {
  workflowId: string;
  source_name: string;
  component_count: number;
  /** Structural digest — changes when the author edits their flow, invalidating a stale draft. */
  definition_fingerprint: string;
  components: FlowComponent[];
}

/**
 * Why a flow could not be turned into a component list. `flow_components_unavailable` carries the
 * specific reason so the UI can be honest rather than saying "something went wrong".
 */
export interface FlowComponentsUnavailable {
  reason: 'parse_error' | 'no_definition' | 'no_components';
}

/** One possible duplicate, as POST /api/tools/check-duplicates returns it (UP-F27/UP-F28). */
export interface DuplicateCandidate {
  toolId: string;
  name: string;
  description: string;
  ownerName: string;
  mine: boolean;
  publicationStatus: string;
  trustLabel: 'Approved' | 'Unverified';
  /** `same_flow` is a fact (same workflow id); `similar` is a soft text hint. */
  matchKind: 'same_flow' | 'similar';
}

export interface CheckDuplicatesResult {
  same_flow: boolean;
  candidates: DuplicateCandidate[];
}

/** The 422 body when the server's own component gate rejects an incomplete submission. */
export interface ComponentDocsIncomplete {
  component_count: number;
  explained_count: number;
  missing: { id: string; numbering: string; label: string }[];
}

/** Ask the backend to walk one flow into its documentable components. */
export function inspectComponents(targetEnvironmentId: string, workflowId: string) {
  return apiPost<InspectComponentsResult>('/api/tools/inspect-components', {
    targetEnvironmentId,
    workflowId,
  });
}

/**
 * Look for possible duplicates of the flow about to be shared. Non-blocking by requirement
 * (UP-F27) — the caller shows the result and lets the builder decide.
 *
 * A POST, not a GET: `name`/`problem_solved` are builder text and AE-F36/DS-F19 forbid recording
 * what someone searched for, so the text stays out of URLs, access logs and browser history.
 */
export function checkDuplicates(args: {
  workflowId: string;
  name?: string;
  problem_solved?: string;
}) {
  return apiPost<CheckDuplicatesResult>('/api/tools/check-duplicates', args);
}

/**
 * Type-ahead suggestions for the global search field (`DS-F20`).
 *
 * A POST, not a GET, for the same reason `checkDuplicates` is: AE-F36 / DS-F19 forbid recording what
 * someone searched for, and this fires as the person types — a query string would put every prefix of
 * every search into access logs.
 *
 * Never throws and never rejects. DS-NFR08 requires that the field "does not slow a page down" and
 * that typing "must never block the page behind it", so any failure — offline, 500, signed out — is
 * an empty dropdown. The person can still submit the query and get the full results view (DS-F18).
 */
export async function fetchSuggestions(q: string): Promise<ToolSuggestion[]> {
  if (!isSearchable(q)) return [];
  try {
    const res = await apiPost<{ suggestions: ToolSuggestion[] }>('/api/tools/suggest', { q });
    if (res.kind !== 'ok') return [];
    const list = res.body?.suggestions;
    // Defensive: the dropdown renders whatever this returns, and MAX_SUGGESTIONS is the requirement.
    return Array.isArray(list) ? list.slice(0, MAX_SUGGESTIONS) : [];
  } catch {
    return [];
  }
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────
// ARTEFACT UPLOAD (Stream A, UP-F04/F08/F09/F10/F11) — the File / Prompt / Link capture path.
//
// This is a SEPARATE pipeline from the Dataverse UploadRequest above: no target env, no workflow id,
// no token, no blueprint. The artefact IS the tool. The server contract is B1's pair of sibling
// routes (routes/tools.ts):
//
//   POST /api/tools/artefact/blob  — the FILE case only. One file's raw BYTES as an
//     application/octet-stream body (bypasses the global 1 MB json cap via a route-scoped
//     express.raw). Returns the minted { blobRef, byteSize, sha256, mimeType } the metadata call
//     then quotes back. Prompt and link artefacts carry no bytes and skip this entirely.
//   POST /api/tools/artefact       — the metadata (json). A discriminated union on artefact_type:
//     file -> a primary blob ref + up to 5 supporting refs; prompt -> prompt_text; link -> link_url.
//
// TRUST POSTURE unchanged from the rest of this file: same-origin, session cookie, no secret ever on
// the wire. The blob bytes stay on the server's local disk (filesystem BlobStore) — no Microsoft
// reach, honouring the security invariant.
// ─────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * One stored-blob reference the wizard got back from the blob step, echoed verbatim into the metadata
 * request. Field names/shapes MIRROR routes/tools.ts `artefactBlobRef` exactly — sha256 travels as
 * lowercase hex (JSON has no Buffer), and nothing here is trusted for safety on its own: the server
 * re-reads the blob head from disk and re-sniffs the magic bytes before writing any row.
 */
export interface ArtefactBlobRef {
  blobRef: string;
  filename: string;
  mimeType: string;
  byteSize: number;
  sha256: string;
}

/** The blob step's success shape (POST /api/tools/artefact/blob 201). */
export interface ArtefactBlobResult {
  blobRef: string;
  byteSize: number;
  sha256: string;
  mimeType: string;
}

/**
 * The builder-authored tool fields common to all three artefact kinds. Shapes/bounds mirror
 * routes/tools.ts `artefactCommon` exactly (which itself mirrors uploadBody), so the client cannot
 * disagree with the server. `setup_instructions` is REQUIRED on this path (there is no component-docs
 * projection to render it from, unlike the Dataverse flow).
 */
export interface ArtefactCommonFields {
  name: string;
  description: string;
  problem_solved: string;
  primary_category_id: number;
  access_requirement_id: number;
  department_id: number;
  setup_instructions: string;
  dependencies: string;
  estimated_time_saved: number;
  /** The data-safety attestation (UP-F23/GC-7). REQUIRED true — the server re-checks it. */
  attested: boolean;
  /** Up to 5 raw tag labels; omitted when the builder added none. */
  tags?: string[];
}

/**
 * The artefact metadata body (json), discriminated on `artefact_type` — mirrors routes/tools.ts
 * `artefactUploadBody`. The file arm carries the blob refs the blob step returned; prompt and link
 * carry their own single payload.
 */
export type ArtefactUploadRequest =
  | (ArtefactCommonFields & {
      artefact_type: 'file';
      primary: ArtefactBlobRef;
      supporting?: ArtefactBlobRef[];
    })
  | (ArtefactCommonFields & { artefact_type: 'prompt'; prompt_text: string })
  | (ArtefactCommonFields & { artefact_type: 'link'; link_url: string });

/**
 * POST /api/tools/artefact success shape (201) — safe: ids + status + the real selected type, never a
 * blob path, sha256 or byte content. The wizard reads `publication_status` to tell the builder which
 * route the tool took (Pending under manual_review vs Approved under self_moderated), exactly as the
 * Dataverse path's `UploadResult` does.
 */
export interface ArtefactUploadResult {
  tool_id: string;
  name: string;
  publication_status: string;
  artefact_type: 'file' | 'prompt' | 'link';
}

/** Progress of one in-flight blob upload, 0..1, or null while indeterminate (before the first event). */
export type BlobProgress = { loaded: number; total: number } | null;

/**
 * Upload one file's raw bytes to the blob step, reporting DETERMINATE progress (UP-F11).
 *
 * WHY XHR AND NOT `apiPost`/`fetch`: fetch has no upload-progress event, and apiPost is json-only. A
 * 250 MB file needs `xhr.upload.onprogress` to drive a real progress bar, and the body must be raw
 * octet-stream so it bypasses the server's global 1 MB json parser. So this is the one place in this
 * client that reaches for XMLHttpRequest rather than the shared fetch helpers.
 *
 * Resolves to the same `ApiResultNoFeature` union the fetch helpers use, so callers branch uniformly.
 * The `onProgress` callback fires with cumulative loaded/total as the browser reports it; `signal`
 * aborts the transfer (Retry / remove-mid-upload) and surfaces as an `aborted` error code.
 */
export function putArtefactBlob(
  file: File,
  opts: { onProgress?: (p: { loaded: number; total: number }) => void; signal?: AbortSignal } = {},
): Promise<ApiResultNoFeature<ArtefactBlobResult>> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/tools/artefact/blob');
    xhr.withCredentials = true;
    xhr.responseType = 'text';
    // The declared content-type is recorded for display only; the server sniffs the real magic bytes.
    // Fall back to octet-stream when the browser gives the File no type.
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.setRequestHeader('Accept', 'application/json');

    if (opts.onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) opts.onProgress?.({ loaded: e.loaded, total: e.total });
      };
    }

    const parseBody = (): { error?: string; detail?: unknown } | ArtefactBlobResult | null => {
      const text = xhr.responseText;
      if (!text || text.trim().length === 0) return null;
      try {
        return JSON.parse(text) as ArtefactBlobResult | { error?: string; detail?: unknown };
      } catch {
        return null;
      }
    };

    xhr.onload = () => {
      if (xhr.status === 401) return resolve({ kind: 'unauthorized' });
      const body = parseBody();
      if (xhr.status >= 200 && xhr.status < 300 && body && 'blobRef' in body) {
        resolve({ kind: 'ok', body });
        return;
      }
      const err = (body && 'error' in body ? body : null) as {
        error?: string;
        detail?: unknown;
      } | null;
      resolve({
        kind: 'error',
        status: xhr.status,
        code: err?.error ?? null,
        detail: err?.detail,
      });
    };
    // Network failure — a status of 0 with no body, distinct from a server error above.
    xhr.onerror = () => resolve({ kind: 'error', status: 0, code: 'network_error' });
    xhr.onabort = () => resolve({ kind: 'error', status: 0, code: 'aborted' });

    if (opts.signal) {
      if (opts.signal.aborted) {
        // Already aborted before we started — never open the socket.
        resolve({ kind: 'error', status: 0, code: 'aborted' });
        return;
      }
      opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.send(file);
  });
}

/** POST the artefact metadata (json). Prompt/link go straight here; file quotes its blob refs. */
export function submitArtefact(body: ArtefactUploadRequest) {
  return apiPost<ArtefactUploadResult>('/api/tools/artefact', body);
}
